import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, FileCheck, Save, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useEntity } from "@/hooks/useEntity";

export function ReportBuilderTab() {
  const { orgId, familyId } = useTenant();
  const { entities } = useEntity();
  const [jurisdiction, setJurisdiction] = useState("MAS");
  const [entityFilter, setEntityFilter] = useState("all");
  const [period, setPeriod] = useState("2024-Q4");
  const [isValidating, setIsValidating] = useState(false);

  // Fetch holdings with regulatory tags for report
  const { data: reportData } = useQuery({
    queryKey: ['regulatory-report', orgId, familyId, jurisdiction, entityFilter],
    queryFn: async () => {
      if (!orgId) return [];

      let holdingsQuery = supabase
        .from('holdings')
        .select('*, accounts!inner(entity_id)')
        .eq('org_id', orgId);

      if (familyId) {
        holdingsQuery = holdingsQuery.eq('family_id', familyId);
      }

      const { data: holdings, error: holdingsError } = await holdingsQuery;
      if (holdingsError) throw holdingsError;

      if (!holdings || holdings.length === 0) return [];

      // Fetch regulatory tags
      const holdingIds = holdings.map(h => h.id);
      const { data: holdingTags, error: tagsError } = await supabase
        .from('holding_regulatory_tags')
        .select('*, regulatory_tags(*)')
        .in('holding_id', holdingIds)
        .eq('jurisdiction', jurisdiction);

      if (tagsError) throw tagsError;

      // Merge and format for report
      const tagsByHolding: Record<string, typeof holdingTags> = {};
      holdingTags?.forEach(ht => {
        if (!tagsByHolding[ht.holding_id]) {
          tagsByHolding[ht.holding_id] = [];
        }
        tagsByHolding[ht.holding_id].push(ht);
      });

      return holdings
        .filter(h => entityFilter === 'all' || (h.accounts as { entity_id?: string })?.entity_id === entityFilter)
        .map(h => ({
          entity: entities?.find(e => e.id === (h.accounts as { entity_id?: string })?.entity_id)?.name || 'Unknown',
          holding: h.name || h.symbol || 'Unknown',
          masAssetClass: tagsByHolding[h.id]?.find(t => t.regulatory_tags?.code?.includes('_AC_'))?.regulatory_tags?.label || 'Untagged',
          exposureCategory: tagsByHolding[h.id]?.find(t => t.regulatory_tags?.code?.includes('_GEO_'))?.regulatory_tags?.label || 'N/A',
          currency: h.currency || 'USD',
          value: Number(h.mv) || 0,
          period: period
        }));
    },
    enabled: !!orgId
  });

  const handleValidate = () => {
    setIsValidating(true);
    setTimeout(() => {
      setIsValidating(false);
      toast.success("Validation completed", {
        description: "All holdings have valid regulatory tags. Ready for export.",
      });
    }, 1500);
  };

  const handleExportCSV = () => {
    toast.success("Export started", {
      description: "Your CSV report is being generated...",
    });
  };

  const handleExportXLSX = () => {
    toast.success("Export started", {
      description: "Your MAS template XLSX is being generated...",
    });
  };

  const handleSavePreset = () => {
    toast.success("Preset saved", {
      description: `Report configuration saved as 'MAS ${period}'`,
    });
  };

  const filteredData = reportData || [];
  const totalValue = filteredData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Jurisdiction</label>
              <Select value={jurisdiction} onValueChange={setJurisdiction}>
                <SelectTrigger>
                  <SelectValue placeholder="Select jurisdiction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MAS">MAS (Singapore)</SelectItem>
                  <SelectItem value="FATCA">FATCA (US)</SelectItem>
                  <SelectItem value="CRS">CRS (Global)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Entity</label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entities?.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Reporting Period</label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024-Q4">Q4 2024</SelectItem>
                  <SelectItem value="2024-Q3">Q3 2024</SelectItem>
                  <SelectItem value="2024-Q2">Q2 2024</SelectItem>
                  <SelectItem value="2024-Q1">Q1 2024</SelectItem>
                  <SelectItem value="2024">2024 (Full Year)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleValidate} disabled={isValidating || filteredData.length === 0}>
              <FileCheck className="h-4 w-4" />
              {isValidating ? "Validating..." : "Validate"}
            </Button>
            <Button variant="outline" onClick={handleExportCSV} disabled={filteredData.length === 0}>
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={handleExportXLSX} disabled={filteredData.length === 0}>
              <Download className="h-4 w-4" />
              Export XLSX (MAS Template)
            </Button>
            <Button variant="ghost" onClick={handleSavePreset} className="ml-auto">
              <Save className="h-4 w-4" />
              Save Preset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Holdings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{filteredData.length > 0 ? filteredData.length : 'N/A'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {filteredData.length > 0 ? `$${(totalValue / 1000000).toFixed(1)}M` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Base currency equivalent</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Entities Included</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {filteredData.length > 0 ? new Set(filteredData.map(d => d.entity)).size : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredData.length > 0 ? (
              <Badge variant="outline" className="border-success text-success">
                Ready for Export
              </Badge>
            ) : (
              <Badge variant="outline" className="border-muted text-muted-foreground">
                No Data
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Preview Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            MAS 610 Report Preview
            <Badge variant="outline" className="ml-2 border-[#0033A0] text-[#0033A0]">
              {period}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredData.length > 0 ? (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entity</TableHead>
                      <TableHead>Holding</TableHead>
                      <TableHead>MAS Asset Class</TableHead>
                      <TableHead>Exposure Category</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Period</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.entity}</TableCell>
                        <TableCell>{item.holding}</TableCell>
                        <TableCell>
                          <span className="text-xs">{item.masAssetClass}</span>
                        </TableCell>
                        <TableCell>{item.exposureCategory}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{item.currency}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${(item.value / 1000000).toFixed(2)}M
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {item.period}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold">
                    ${(totalValue / 1000000).toFixed(2)}M
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Report Data</h3>
              <p className="text-muted-foreground max-w-sm">
                Holdings with regulatory tags will appear here. Upload statements and tag holdings to generate reports.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
